import { useEffect, useRef, useState } from 'react';

import { EllipsisVerticalIcon } from '../icons';

interface MissionCardMenuAction {
  readonly label: string;
  readonly onSelect: () => void;
  readonly danger?: boolean;
}

interface MissionCardMenuProps {
  readonly label: string;
  readonly actions: readonly MissionCardMenuAction[];
  readonly disabled?: boolean;
}

/** A lightweight disclosure of plain buttons — not a full ARIA menu widget, since that would
 * require roving-tabindex arrow-key navigation this list of 1-3 actions does not need. */
export function MissionCardMenu({ label, actions, disabled = false }: MissionCardMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (actions.length === 0) return null;

  return (
    <div className="mission-card__menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="icon-button mission-card__menu-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <EllipsisVerticalIcon width={18} height={18} />
      </button>
      {open ? (
        <div className="mission-card__menu-popover">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={`mission-card__menu-item${action.danger ? ' mission-card__menu-item--danger' : ''}`}
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                action.onSelect();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
